import { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import styles from "../css/LearningTypeSettingScreen.module.css";
import { LearningType } from "../model/LearningType";
import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import ListSettingView from "../../common/ListSettingView";
import ActionMenu from "../../../components/ui/ActionMenu";
import { useBackToClose } from "../../../hooks/useBackToClose";

export interface LearningTypeSettingOption {
  label: string;
  isSelected: (learningType: LearningType) => boolean;
  onSelect: (learningType: LearningType) => void;
}

interface LearningTypeSettingScreenProps {
  open: boolean;
  title: string;
  // 강조 표시하고 맨 위에 보여줄 학습
  currentLearningType: LearningType | null;
  learningTypes: LearningType[];
  getValueText: (learningType: LearningType) => string;
  getOptions: (learningType: LearningType) => LearningTypeSettingOption[];
  handleOnUpdateValue?: () => void;
  handleOnClose: () => void;
}

// 메모 표기, 퀴즈 방식처럼 학습별로 따로 저장되는 설정을 한 화면에서 보여준다.
export default function LearningTypeSettingScreen(
  props: LearningTypeSettingScreenProps
) {
  useBackToClose(props.open, props.handleOnClose);
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedLearningType, setSelectedLearningType] =
    useState<LearningType | null>(null);
  // 값 변경 후 다시 그리기 위한 상태
  const [, setVersion] = useState(0);

  if (props.open === false) {
    return null;
  }

  const orderedLearningTypes =
    props.currentLearningType !== null &&
    props.learningTypes.includes(props.currentLearningType)
      ? [
          props.currentLearningType,
          ...props.learningTypes.filter(
            (type) => type !== props.currentLearningType
          ),
        ]
      : props.learningTypes;

  function getLearningTypeTitle(learningType: LearningType): string {
    switch (learningType) {
      case LearningType.flashcards:
        return t("Flashcard");
      case LearningType.multiplechoice:
        return t("MultipleChoiceTitle");
      case LearningType.spellingQuiz:
        return t("SpellingQuiz");
    }
  }

  function closeMenu() {
    setAnchorEl(null);
    setSelectedLearningType(null);
  }

  return (
    <>
      <div className={styles.stack}>
        <div className={styles.container} onClick={props.handleOnClose}>
          <div
            className={styles.contentContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <ContainerNavigationView
              open={true}
              title={props.title}
              leftImageButtonImage={CloseIcon}
              onClickLeftImageButton={props.handleOnClose}
            />

            <div className={styles.settingItemContainer}>
              {orderedLearningTypes.map((learningType) => (
                <div
                  key={learningType}
                  className={classNames(styles.settingContainer, {
                    [styles.settingContainerActive]:
                      learningType === props.currentLearningType,
                  })}
                >
                  <ListSettingView
                    title={getLearningTypeTitle(learningType)}
                    value={props.getValueText(learningType)}
                    valueStyle="stringValue"
                    handleOnClick={(e) => {
                      setAnchorEl(e.currentTarget);
                      setSelectedLearningType(learningType);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ActionMenu
        open={selectedLearningType !== null}
        anchorEl={anchorEl}
        title={
          selectedLearningType !== null
            ? getLearningTypeTitle(selectedLearningType)
            : undefined
        }
        items={
          selectedLearningType !== null
            ? props.getOptions(selectedLearningType).map((option) => ({
                label: option.label,
                selected: option.isSelected(selectedLearningType),
                onSelect: () => {
                  option.onSelect(selectedLearningType);
                  closeMenu();
                  setVersion((v) => v + 1);
                  props.handleOnUpdateValue?.();
                },
              }))
            : []
        }
        onClose={closeMenu}
      />
    </>
  );
}
